import React, { memo, useCallback, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@context/themeContext';
import { themeColors } from '@styles/theme';
import { useGroupSelectionContext } from '@context/groupSelectionContext';
import { isErrorResponse, safelyParseJson } from '@utils/utils';
import { useToastContext } from '@context/toastContext';
import { IconButton } from '@components/buttons/IconButton/IconButton';
import { MergeSelectionSetsIcon } from '@icons/group-selection/MergeSelectionSetsIcon';
import Modal from '@components/Modal/Modal';
import GroupSelectionSetsMergeForm from './GroupSelectionSetsMergeForm';

type GroupSelectionSetsMergeActionProps = {
    selectedForMergeIds: string[];
    onSave: (id: string) => void;
    onMergeSuccess: () => void;
};

const GroupSelectionSetsMergeAction = memo(
    ({ selectedForMergeIds, onSave, onMergeSuccess }: GroupSelectionSetsMergeActionProps) => {
        const [name, setName] = useState('');
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [isMerging, setIsMerging] = useState(false);
        const { mergeGroupSelectionSet, getGroupSelectionSets } = useGroupSelectionContext();
        const { addToast } = useToastContext();
        const { t } = useTranslation();
        const { theme } = useTheme();

        const disabledBtn = isMerging || selectedForMergeIds.length !== 2;

        const onSetNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
            setName(event.target.value);
        }, []);

        const onFormSubmit = useCallback(
            async (event: React.FormEvent<HTMLFormElement>) => {
                const [firstSelectionSetId, secondSelectionSetId] = selectedForMergeIds;
                if (!firstSelectionSetId || !secondSelectionSetId) {
                    return;
                }
                setIsMerging(true);
                const formData = new FormData(event.currentTarget);
                const formName = String(formData.get('name') ?? '').trim();
                if (!formName) {
                    setIsMerging(false);
                    return;
                }

                const resp = await mergeGroupSelectionSet(
                    formName,
                    firstSelectionSetId,
                    secondSelectionSetId
                );

                if (isErrorResponse(resp)) {
                    try {
                        const parsedErr =
                            typeof resp.error === 'string'
                                ? safelyParseJson(resp.error)?.error
                                : resp.error.error;
                        addToast(parsedErr ?? t('common.save-error'), 'error');
                    } catch (e) {
                        console.log(
                            'Replan. GroupSelectionSetsMergeAction. Error parsing error response',
                            resp,
                            e
                        );
                    }
                } else {
                    addToast(t('common.save-successful'), 'success');
                    setName('');
                    setIsModalOpen(false);
                    onMergeSuccess();
                    await getGroupSelectionSets();
                    if (resp.id) {
                        onSave(resp.id);
                    }
                }
                setIsMerging(false);
            },
            [
                selectedForMergeIds,
                mergeGroupSelectionSet,
                getGroupSelectionSets,
                addToast,
                t,
                onMergeSuccess,
                onSave,
            ]
        );

        const openModal = useCallback(() => {
            setIsModalOpen(true);
        }, []);

        return (
            <>
                <Tooltip title={t('road-network.group-selection-merge-selection-sets-btn')}>
                    <div>
                        <IconButton
                            icon={() => (
                                <MergeSelectionSetsIcon
                                    color={
                                        disabledBtn
                                            ? themeColors.grey3
                                            : theme === 'light'
                                              ? themeColors.bluePurple
                                              : themeColors.white
                                    }
                                />
                            )}
                            width={24}
                            height={24}
                            borderless
                            onClick={openModal}
                            disabled={disabledBtn}
                        />
                    </div>
                </Tooltip>
                {isModalOpen ? (
                    <Modal
                        title={t('road-network.group-selection-merge-selection-sets-modal')}
                        setVisible={setIsModalOpen}
                        narrow
                    >
                        <GroupSelectionSetsMergeForm
                            setName={name}
                            onSetNameChange={onSetNameChange}
                            onFormSubmit={onFormSubmit}
                            disabled={disabledBtn || !name.trim()}
                        />
                    </Modal>
                ) : null}
            </>
        );
    }
);

export default GroupSelectionSetsMergeAction;
